import {
  getAccessPointRoadNodePosition,
  getLayoutObjectRoadNodePosition,
  isRoadConnectableLayoutObject,
} from '@yard/utils/road-management.util'
import { MutableRefObject, useCallback, useEffect } from 'react'
import { Node } from 'reactflow'
import {
  LayoutObjectWithActions,
  RoadNodeWithActions,
  YardBlockWithOccupanciesAndActions,
} from '../models/yard-view.model'
import { RoadNetworkViewStore } from '../stores/nodes/road-network.view-store'

interface UseYardRoadDrawingResult {
  onPaneClick: (event: React.MouseEvent) => void
  onNodeClick: (event: React.MouseEvent, node: Node) => void
  onPaneContextMenu: (event: React.MouseEvent) => void
}

export const useYardRoadDrawing = (
  roadNetworkViewStore: RoadNetworkViewStore,
  screenToFlowPosition: (point: { x: number; y: number }) => { x: number; y: number },
  pendingClickTimer: MutableRefObject<ReturnType<typeof setTimeout> | null>,
): UseYardRoadDrawingResult => {
  const onPaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!roadNetworkViewStore.isDrawingMode) return

      // If the click landed on a road node element, onNodeClick handles it — skip here
      const elementAtClick = document.elementFromPoint(event.clientX, event.clientY)
      if (elementAtClick?.closest('[data-road-node]')) return
      if (elementAtClick?.closest('[data-road-segment]')) return

      if (pendingClickTimer.current !== null) {
        clearTimeout(pendingClickTimer.current)
        pendingClickTimer.current = null
      }

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      pendingClickTimer.current = setTimeout(() => {
        pendingClickTimer.current = null
        roadNetworkViewStore.placeNode(position)
      }, 50)
    },
    [roadNetworkViewStore, screenToFlowPosition, pendingClickTimer],
  )

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!roadNetworkViewStore.isDrawingMode) return

      if (isRoadConnectableLayoutObject(node)) {
        if (pendingClickTimer.current !== null) {
          clearTimeout(pendingClickTimer.current)
          pendingClickTimer.current = null
        }
        const layoutObjectData = node.data as LayoutObjectWithActions

        const referencePoint = roadNetworkViewStore.pendingNode
          ? roadNetworkViewStore.pendingNode.layoutPosition
          : screenToFlowPosition({ x: event.clientX, y: event.clientY })

        roadNetworkViewStore.placeNode(
          getLayoutObjectRoadNodePosition(layoutObjectData, referencePoint),
          layoutObjectData.id,
        )
        return
      }

      if (node.type === 'yardBlock') {
        if (pendingClickTimer.current !== null) {
          clearTimeout(pendingClickTimer.current)
          pendingClickTimer.current = null
        }

        // Detect if the click landed on an access point bar via its data attribute.
        const barEl = (event.target as Element).closest('[data-access-point-id]')

        if (barEl) {
          const accessPointId = barEl.getAttribute('data-access-point-id')
          const blockData = node.data as YardBlockWithOccupanciesAndActions
          const accessPoint = blockData.accessPoints?.find(ap => ap.id === accessPointId)

          if (accessPoint) {
            const barDomRect = barEl.getBoundingClientRect()
            const barFlowCenter = screenToFlowPosition({
              x: barDomRect.left + barDomRect.width / 2,
              y: barDomRect.top + barDomRect.height / 2,
            })
            const position = getAccessPointRoadNodePosition(accessPoint.side, barFlowCenter)
            roadNetworkViewStore.placeNode(position, null, accessPointId)
          }
        }
        return
      }

      if (node.type !== 'roadNode') {
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
        roadNetworkViewStore.placeNode(position)
        return
      }

      if (pendingClickTimer.current !== null) {
        clearTimeout(pendingClickTimer.current)
        pendingClickTimer.current = null
      }

      const originalId = (node.data as RoadNodeWithActions).id
      roadNetworkViewStore.connectToNode(originalId)
    },
    [roadNetworkViewStore, screenToFlowPosition, pendingClickTimer],
  )

  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent) => {
      if (!roadNetworkViewStore.isDrawingMode) return
      event.preventDefault()
      roadNetworkViewStore.clearPendingNode()
    },
    [roadNetworkViewStore],
  )

  useEffect(() => {
    if (!roadNetworkViewStore.isDrawingMode) {
      if (pendingClickTimer.current !== null) {
        clearTimeout(pendingClickTimer.current)
        pendingClickTimer.current = null
      }
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (roadNetworkViewStore.pendingNodeId) {
          roadNetworkViewStore.clearPendingNode()
        } else {
          roadNetworkViewStore.exitDrawingMode()
        }
      }
      if (event.key === 'Delete' && roadNetworkViewStore.pendingNodeId) {
        roadNetworkViewStore.deleteNode(roadNetworkViewStore.pendingNodeId)
        roadNetworkViewStore.clearPendingNode()
      }
    }

    const handleDoubleClick = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.react-flow__pane')) return
      if ((event.target as Element).closest('[data-road-segment]')) return
      if (pendingClickTimer.current !== null) {
        clearTimeout(pendingClickTimer.current)
        pendingClickTimer.current = null
      }
      roadNetworkViewStore.clearPendingNode()
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('dblclick', handleDoubleClick)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('dblclick', handleDoubleClick)
    }
  }, [roadNetworkViewStore.isDrawingMode, roadNetworkViewStore, pendingClickTimer])

  return { onPaneClick, onNodeClick, onPaneContextMenu }
}
